import { apiClient } from "./client";
import { Project } from "./projects";
import { User } from "./auth";
import { Timesheet } from "./timesheets";
import { Did } from "@/types";

export interface Photo {
  id: Did;
  timesheet_id?: Did;
  project_id: Did | null;
  user_id: Did;
  path: string;
  url: string;
  thumbnail_url?: string;
  caption?: string;
  taken_at?: string;
  meta?: {
    lat?: number;
    lng?: number;
    device?: string;
  };
  created_at: string;
  updated_at: string;
  timesheet?: Timesheet;
  project?: Project;
  user?: User;
}

export interface UploadPhotoRequest {
  project_id?: Did | null;
  timesheet_id?: Did;
  caption?: string;
  photo: File | Blob | string;
  lat?: number;
  lng?: number;
}

/**
 * Upload a photo
 */
export const uploadPhoto = async (
  request: UploadPhotoRequest
): Promise<Photo> => {
  const formData = new FormData();
  formData.append("photo", request.photo);
  if (request.project_id != null) {
    formData.append("project_id", request.project_id.toString());
  }

  if (request.timesheet_id) {
    formData.append("timesheet_id", request.timesheet_id.toString());
  }
  if (request.caption) {
    formData.append("caption", request.caption);
  }
  if (request.lat && request.lng) {
    formData.append("lat", request.lat.toString());
    formData.append("lng", request.lng.toString());
  }

  const { data } = await apiClient.post<{ data: Photo }>("/photos", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data.data;
};

/**
 * Get photos for a project
 */
export const getProjectPhotos = async (projectId: Did): Promise<Photo[]> => {
  const { data } = await apiClient.get<{ data: Photo[] }>("/photos", {
    params: { project_id: projectId },
  });
  return data.data;
};

/**
 * Get my photos (authenticated user)
 */
export const getRecentPhotos = async (): Promise<Photo[]> => {
  const { data } = await apiClient.get<{ data: Photo[] }>("/photos/recent");
  return data.data;
};

/**
 * Get a single photo
 */
export const getPhoto = async (id: Did): Promise<Photo> => {
  const { data } = await apiClient.get<{ data: Photo }>(`/photos/${id}`);
  return data.data;
};

/**
 * Update photo caption
 */
export const updatePhotoCaption = async (
  id: Did,
  caption: string
): Promise<Photo> => {
  const { data } = await apiClient.put<{ data: Photo }>(`/photos/${id}`, {
    caption,
  });
  return data.data;
};

/**
 * Delete a photo
 */
export const deletePhoto = async (id: Did): Promise<void> => {
  await apiClient.delete(`/photos/${id}`);
};
